import { useState, useCallback } from 'react';
import { resultsApi } from '@/services/api';

export const useSaveShare = () => {
  const [isSaving, setIsSaving] = useState(false);
  const [shareCode, setShareCode] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const saveResults = useCallback(async (answers, platforms, comparisonIds = []) => {
    setIsSaving(true);
    setSaveError(null);
    
    try {
      const formattedPlatforms = platforms.map(p => ({
        id: p.id,
        name: p.name,
        provider: p.provider,
        summary: p.summary,
        suitability: p.suitability || 'caution',
        percentage: p.percentage || 50,
        cost_band: p.costBand || 2,
        reasons_suitable: p.reasons?.suitable || [],
        reasons_concerns: p.reasons?.concerns || [],
      }));
      
      const result = await resultsApi.save({
        answers,
        platforms: formattedPlatforms,
        comparison_ids: comparisonIds,
      });
      
      setShareCode(result.share_code);
      return result.share_code;
    } catch (error) {
      console.error('Failed to save results:', error);
      setSaveError(error.message);
      return null;
    } finally {
      setIsSaving(false);
    }
  }, []);

  const loadSharedResults = useCallback(async (code) => {
    try {
      const result = await resultsApi.get(code);
      return result;
    } catch (error) {
      console.error('Failed to load shared results:', error);
      return null;
    }
  }, []);

  const getShareUrl = useCallback((code) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/shared/${code}`;
  }, []);

  const copyShareLink = useCallback(async (code) => {
    const url = getShareUrl(code);
    try {
      await navigator.clipboard.writeText(url);
      return true;
    } catch (error) {
      console.error('Failed to copy:', error);
      return false;
    }
  }, [getShareUrl]);

  const clearShare = useCallback(() => {
    setShareCode(null);
    setSaveError(null);
  }, []);

  return {
    isSaving,
    shareCode,
    saveError,
    saveResults,
    loadSharedResults,
    getShareUrl,
    copyShareLink,
    clearShare,
  };
};
